/**
 * WindRenderer — Canvas-based wind particle animation
 *
 * Inspired by earth.nullschool.net. Renders wind particles on an HTML canvas
 * that overlays the map. All particle state is managed outside React for
 * maximum performance (no React re-renders, no GC pressure from state updates).
 *
 * Key design decisions:
 * - Uses a semi-transparent canvas that fades previous frames (trail effect)
 * - Particles are just 1-2px lines drawn each frame
 * - The "trail" comes from the canvas not being fully cleared each frame
 * - Movement is computed in screen pixels then unprojected to geo coords
 */

import type { Map as MapLibreMap } from 'maplibre-gl';
import type { WindData } from './wind';
import { interpolateWind, getWindSpeedColor } from './wind';

interface Particle {
  lng: number;
  lat: number;
  age: number;
  maxAge: number;
}

interface RendererOptions {
  particleCount?: number;
  fadeOpacity?: number;
  lineWidth?: number;
  speedFactor?: number;
  particleMaxAge?: number;
  opacity?: number;
}

const DEFAULTS: Required<RendererOptions> = {
  particleCount: 4000,
  fadeOpacity: 0.96,
  lineWidth: 1.4,
  speedFactor: 0.8,        // Pixels per m/s of wind speed per frame (at 60fps)
  particleMaxAge: 200,
  opacity: 0.8,
};

export class WindRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private map: MapLibreMap;
  private windData: WindData | null = null;
  private particles: Particle[] = [];
  private frameId: number = 0;
  private options: Required<RendererOptions>;
  private lastTime: number = 0;
  private isRunning: boolean = false;
  private resizeObserver: ResizeObserver;

  constructor(map: MapLibreMap, options?: RendererOptions) {
    this.map = map;
    this.options = { ...DEFAULTS, ...options };

    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '10';

    const ctx = this.canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    // Append to the map's outer container (not canvasContainer) to avoid
    // being clipped or covered by maplibre's internal canvas management.
    const container = map.getContainer();
    container.appendChild(this.canvas);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();

    this.handleMoveStart = this.handleMoveStart.bind(this);
    this.handleMoveEnd = this.handleMoveEnd.bind(this);
    map.on('movestart', this.handleMoveStart);
    map.on('moveend', this.handleMoveEnd);
    map.on('zoomstart', this.handleMoveStart);
    map.on('zoomend', this.handleMoveEnd);
  }

  private _moving = false;

  private handleMoveStart() {
    this._moving = true;
  }

  private handleMoveEnd() {
    this._moving = false;
    this.initParticles();
  }

  private resize() {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth;
    const h = container.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.initParticles();
  }

  setWindData(data: WindData | null) {
    this.windData = data;
    if (data) {
      this.initParticles();
    } else {
      this.particles = [];
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  setOpacity(opacity: number) {
    this.options.opacity = Math.max(0, Math.min(1, opacity));
    this.canvas.style.opacity = String(this.options.opacity);
  }

  private initParticles() {
    if (!this.windData) return;

    const bounds = this.getMapBounds();
    if (!bounds) return;

    this.particles = [];
    for (let i = 0; i < this.options.particleCount; i++) {
      this.particles.push(this.randomParticle(bounds));
    }
  }

  private randomParticle(bounds: { west: number; east: number; south: number; north: number }): Particle {
    return {
      lng: bounds.west + Math.random() * (bounds.east - bounds.west),
      lat: bounds.south + Math.random() * (bounds.north - bounds.south),
      age: Math.floor(Math.random() * this.options.particleMaxAge),
      maxAge: this.options.particleMaxAge + Math.floor(Math.random() * this.options.particleMaxAge * 0.4),
    };
  }

  private getMapBounds() {
    const b = this.map.getBounds();
    if (!b) return null;
    const lngPad = (b.getEast() - b.getWest()) * 0.1;
    const latPad = (b.getNorth() - b.getSouth()) * 0.1;
    return {
      west: b.getWest() - lngPad,
      east: b.getEast() + lngPad,
      south: Math.max(-85, b.getSouth() - latPad),
      north: Math.min(85, b.getNorth() + latPad),
    };
  }

  private _loggedFirstFrame = false;

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this._loggedFirstFrame = false;
    this.lastTime = performance.now();
    this.canvas.style.opacity = String(this.options.opacity);
    this.canvas.style.display = 'block';
    console.log(`[WindRenderer] Started — ${this.particles.length} particles, canvas ${this.canvas.width}x${this.canvas.height}`);
    this.animate(this.lastTime);
  }

  stop() {
    this.isRunning = false;
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvas.style.display = 'none';
  }

  destroy() {
    this.stop();
    this.resizeObserver.disconnect();
    this.map.off('movestart', this.handleMoveStart);
    this.map.off('moveend', this.handleMoveEnd);
    this.map.off('zoomstart', this.handleMoveStart);
    this.map.off('zoomend', this.handleMoveEnd);
    this.canvas.remove();
  }

  private animate = (time: number) => {
    if (!this.isRunning) return;

    const dt = Math.min((time - this.lastTime) / 16.67, 2);
    this.lastTime = time;

    if (this.windData && this.particles.length > 0) {
      this.drawFrame(dt);
    }

    this.frameId = requestAnimationFrame(this.animate);
  };

  private drawFrame(dt: number) {
    const ctx = this.ctx;
    const data = this.windData!;
    const w = this.canvas.width / (window.devicePixelRatio || 1);
    const h = this.canvas.height / (window.devicePixelRatio || 1);

    // Fade previous frame — creates trail effect.
    // During map movement, fade faster to avoid smearing.
    const fade = this._moving ? 0.7 : this.options.fadeOpacity;
    const prev = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = `rgba(0, 0, 0, ${fade})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = prev;

    const colorMap = new Map<string, { sx: number; sy: number; ex: number; ey: number }[]>();

    const bounds = this.getMapBounds();
    if (!bounds) return;

    const pixelsPerMs = this.options.speedFactor * dt;

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      p.age += dt;

      if (p.age >= p.maxAge) {
        this.particles[i] = this.randomParticle(bounds);
        continue;
      }

      // Get wind at particle position (u=east, v=north in m/s)
      const [wu, wv] = interpolateWind(p.lng, p.lat, data);
      const speed = Math.sqrt(wu * wu + wv * wv);

      if (speed < 0.2) continue;

      // Project current position to screen pixels
      const screenPos = this.map.project([p.lng, p.lat]);

      // Move in SCREEN PIXELS based on wind direction and speed
      // wu = eastward component (positive = right on screen)
      // wv = northward component (positive = UP, but screen Y is inverted)
      const pixelDist = speed * pixelsPerMs;
      const nx = wu / speed; // normalized direction
      const ny = wv / speed;
      const endX = screenPos.x + nx * pixelDist;
      const endY = screenPos.y - ny * pixelDist; // negative because screen Y is flipped

      // Check if on screen
      const margin = 20;
      if (endX < -margin || endX > w + margin || endY < -margin || endY > h + margin) {
        this.particles[i] = this.randomParticle(bounds);
        continue;
      }

      // Unproject end position back to geo coordinates for next frame
      const endGeo = this.map.unproject([endX, endY]);
      p.lng = endGeo.lng;
      p.lat = endGeo.lat;

      // Color by speed
      const color = getWindSpeedColor(speed);
      const ageFade = p.age < 15
        ? p.age / 15
        : p.age > p.maxAge - 20
          ? (p.maxAge - p.age) / 20
          : 1;
      // alpha must be 0-1 for CSS rgba()
      const alpha = Math.max(0.12, ageFade * 0.85).toFixed(2);
      const colorKey = `${color[0]},${color[1]},${color[2]},${alpha}`;

      if (!colorMap.has(colorKey)) {
        colorMap.set(colorKey, []);
      }
      colorMap.get(colorKey)!.push({
        sx: screenPos.x,
        sy: screenPos.y,
        ex: endX,
        ey: endY,
      });
    }

    // Batch draw by color
    ctx.lineWidth = this.options.lineWidth;
    ctx.lineCap = 'round';

    if (!this._loggedFirstFrame && colorMap.size > 0) {
      this._loggedFirstFrame = true;
      let totalSegs = 0;
      for (const segs of colorMap.values()) totalSegs += segs.length;
      console.log(`[WindRenderer] Drawing — ${colorMap.size} colors, ${totalSegs} segments`);
    }

    for (const [colorKey, segments] of colorMap) {
      ctx.strokeStyle = `rgba(${colorKey})`;
      ctx.beginPath();
      for (const seg of segments) {
        ctx.moveTo(seg.sx, seg.sy);
        ctx.lineTo(seg.ex, seg.ey);
      }
      ctx.stroke();
    }
  }
}
